import { gotoService } from "@/lib/navigation";
import { Button, Select, Group, SelectProps } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SERVICE_TEMPLATES } from "@/types/templates";
import { SERVICE_CATEGORIES } from "@turizi/shared";
import { Business, Service } from "@turizi/shared";
import { TAG_ICONS } from "@/lib/tag-utils";
import { IconCheck, IconTag } from "@tabler/icons-react";
import BusinessSelector from "./business-selector";
import { useCreateService } from "@/hooks/api-hooks";
import MyBusinessSelector from "./my-business-selector";
import { useActiveBusiness } from "@/pages/wrapper";


interface Props {
    onCreate?: () => void;
    size?: 'sm' | 'md' | 'lg';
}

export default function ModalNewService({
    onCreate,
    size = 'md',
}: Props): JSX.Element {
    const { t } = useTranslation();
    const { activeBusiness, businesses } = useActiveBusiness();
    const defaultBusiness = activeBusiness || (businesses.length === 1 ? businesses[0] : null);

    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(() => defaultBusiness);
    const [draft, setDraft] = useState<Partial<Service>>(
        {
            seller_id: defaultBusiness?.id,
            owner_id: defaultBusiness?.tenant_business?.id || defaultBusiness?.id,
            category: undefined,
        }
    );

    useEffect(() => {
        if (!selectedBusiness && defaultBusiness) {
            setSelectedBusiness(defaultBusiness);
            setDraft((prev) => ({
                ...prev,
                seller_id: defaultBusiness.id,
                owner_id: defaultBusiness.tenant_business?.id || defaultBusiness.id,
            }));
        }
    }, [defaultBusiness, selectedBusiness]);

    const canSave = !!draft.category && !!selectedBusiness;

    const createService = useCreateService();

    const createNewService = () => {
        if (!canSave) return;
        createService.mutate(draft, {
            onSuccess: (response) => {
                if (response.data) {
                    onCreate?.();
                    gotoService(response.data.id);
                }
            }
        });
    }

    const iconProps = {
        stroke: 1.5,
        color: 'currentColor',
        opacity: 0.6,
        size: 18,
    };

    const renderSelectOption: SelectProps['renderOption'] = ({ option, checked }) => {
        const Icon = TAG_ICONS[option.value as keyof typeof TAG_ICONS] || IconTag;
        return (
            <Group flex="1" gap="xs">
                <Icon {...iconProps} />
                {option.label}
                {checked && <IconCheck style={{ marginInlineStart: 'auto' }} {...iconProps} />}
            </Group>
        )
    };

    useEffect(() => {
        if (!draft.category) return;
        const template = SERVICE_TEMPLATES[draft.category];
        setDraft({
            ...draft,
            ...template
        });

    }, [draft.category]);

    return (
        <div>
            <div className="space-y-4">
                <MyBusinessSelector
                    onSelect={(business: Business) => {
                        setSelectedBusiness(business);
                        setDraft((prev) => ({
                            ...prev,
                            seller_id: business.id,
                            owner_id: business.tenant_business?.id || business.id,
                        }));
                    }}
                />
                <div>
                    <Select
                        searchable
                        className="font-bold w-100"
                        size={size}
                        label={t('category')}
                        placeholder={t('select_service_type')}
                        value={draft.category}
                        data={Object.entries(SERVICE_CATEGORIES).map(([key, _]) => ({
                            label: t(key),
                            value: key,
                        }))}
                        onChange={(value) => setDraft({ ...draft, category: value })}
                        renderOption={renderSelectOption}
                    />
                </div>
            </div>
            {
                !!draft.category && (
                    <div className="flex justify-end mt-8">
                        <Button
                            size="sm"
                            radius="lg"
                            onClick={createNewService}
                            loading={createService.isPending}
                            disabled={!canSave}
                            color="black"
                        >
                            {t('create_service')}
                        </Button>
                    </div>
                )
            }
        </div>
    );
}